// src/pages/admin/AdminMatchingPairs.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner, useConfirm } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, ChevronDown, ChevronRight } from 'lucide-react';

const DATATYPES_SHORT = {
  EXACT_MATCH: 'Exact',
  CATEGORICAL_ORDINAL: 'Ordinal',
  CATEGORICAL_PROXIMITY: 'Proximity',
  CATEGORICAL_SET: 'Set',
  CONTINUOUS_NUMERIC: 'Numeric',
  FREE_TEXT_SEMANTIC: 'Semantic',
};

// ── Ordinal Distance Config ──────────────────────────────
const OrdinalDistanceEditor = ({ bands, value, onChange }) => {
  // value is an array of floats: distance between band[i] and band[i+1]
  const distances = value && value.length === bands.length - 1
    ? value
    : Array(Math.max(0, bands.length - 1)).fill(1);

  const handleChange = (idx, val) => {
    const next = [...distances];
    next[idx] = parseFloat(val) || 0;
    onChange(next);
  };

  if (!bands || bands.length < 2) return null;

  return (
    <div style={{ marginTop: 8, padding: 10, background: '#f8f9fa', borderRadius: 6, border: '1px solid #eee' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#3D80CC', marginBottom: 6, textTransform: 'uppercase' }}>
        Band Distances
      </div>
      <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
        Specify the relative distance between adjacent bands (higher = greater penalty for mismatch).
      </div>
      {bands.slice(0, -1).map((band, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: '#555', minWidth: 80, textAlign: 'right' }}>{band}</span>
          <span style={{ fontSize: 11, color: '#999' }}>→</span>
          <input type="number" min="0" step="0.1"
            value={distances[idx]}
            onChange={e => handleChange(idx, e.target.value)}
            style={{ width: 60, fontSize: 12, padding: '2px 6px', border: '1px solid #ccc', borderRadius: 4, textAlign: 'center' }}
          />
          <span style={{ fontSize: 11, color: '#999' }}>→</span>
          <span style={{ fontSize: 11, color: '#555' }}>{bands[idx + 1]}</span>
        </div>
      ))}
    </div>
  );
};

// ── DMP Form Modal ───────────────────────────────────────
const DmpModal = ({ open, onClose, onSave, initial, allFdtas, saving }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFdtas, setSelectedFdtas] = useState([]); // [{ fdtaId, ordinalDistance? }]

  useEffect(() => {
    if (open) {
      if (initial) {
        setName(initial.name);
        setDescription(initial.description || '');
        setSelectedFdtas(
          (initial.fdtaLinks || []).map(l => ({
            fdtaId: l.fdta.id,
            ordinalDistance: l.ordinalDistance || null,
          }))
        );
      } else {
        setName(''); setDescription(''); setSelectedFdtas([]);
      }
    }
  }, [open, initial]);

  if (!open) return null;

  const cvFdtas = allFdtas.filter(f => f.side === 'CV');
  const posFdtas = allFdtas.filter(f => f.side === 'POSITION');
  const selectedIds = new Set(selectedFdtas.map(s => s.fdtaId));

  const hasCv = selectedFdtas.some(s => allFdtas.find(f => f.id === s.fdtaId)?.side === 'CV');
  const hasPos = selectedFdtas.some(s => allFdtas.find(f => f.id === s.fdtaId)?.side === 'POSITION');
  const canSave = name.trim() && hasCv && hasPos;

  const toggleFdta = (fdtaId) => {
    if (selectedIds.has(fdtaId)) {
      setSelectedFdtas(selectedFdtas.filter(s => s.fdtaId !== fdtaId));
    } else {
      setSelectedFdtas([...selectedFdtas, { fdtaId, ordinalDistance: null }]);
    }
  };

  const updateOrdinalDistance = (fdtaId, distances) => {
    setSelectedFdtas(selectedFdtas.map(s =>
      s.fdtaId === fdtaId ? { ...s, ordinalDistance: distances } : s
    ));
  };

  const handleSave = () => {
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      fdtas: selectedFdtas.map((s, i) => ({
        fdtaId: s.fdtaId,
        ordinalDistance: s.ordinalDistance,
        sortOrder: i,
      })),
    });
  };

  const renderFdtaList = (fdtas, sideLabel) => (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#3D80CC', textTransform: 'uppercase', marginBottom: 8 }}>
        {sideLabel}
      </div>
      {fdtas.length === 0 ? (
        <div style={{ fontSize: 12, color: '#999', padding: '8px 0' }}>No field associations available for this side.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {fdtas.map(fdta => {
            const isSelected = selectedIds.has(fdta.id);
            const isOrdinal = fdta.datatype === 'CATEGORICAL_ORDINAL';
            const sel = selectedFdtas.find(s => s.fdtaId === fdta.id);
            return (
              <div key={fdta.id}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                  borderRadius: 6, cursor: 'pointer',
                  background: isSelected ? '#E8F4FD' : 'transparent',
                  border: `1px solid ${isSelected ? '#3D80CC' : '#eee'}`,
                }}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleFdta(fdta.id)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{fdta.name}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>
                      {DATATYPES_SHORT[fdta.datatype] || fdta.datatype}
                      {isOrdinal && fdta.ordinalBands && (
                        <span> — {fdta.ordinalBands.join(' → ')}</span>
                      )}
                    </div>
                  </div>
                </label>
                {isSelected && isOrdinal && Array.isArray(fdta.ordinalBands) && fdta.ordinalBands.length >= 2 && (
                  <OrdinalDistanceEditor
                    bands={fdta.ordinalBands}
                    value={sel?.ordinalDistance}
                    onChange={(d) => updateOrdinalDistance(fdta.id, d)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 600, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>{initial ? 'Edit' : 'Create'} Matching Pair</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }}>
            <X size={18} />
          </button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', flex: 1 }}>

          <div>
            <label className="form-label">Name <span style={{ color: '#CC0000', marginLeft: 2 }}>*</span></label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Skills Match" />
          </div>

          <div>
            <label className="form-label">Description</label>
            <input className="form-input" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Optional description" />
          </div>

          <div style={{ fontSize: 12, color: '#555', padding: '8px 12px', background: '#FFF8E1', borderRadius: 6, border: '1px solid #FFE082' }}>
            Select at least one field association from each side (CV and Position).
          </div>

          {renderFdtaList(cvFdtas, 'CV Side Fields')}
          {renderFdtaList(posFdtas, 'Position Side Fields')}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <Spinner size={14} /> : (initial ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────
export const AdminMatchingPairs = () => {
  const [dmps, setDmps] = useState([]);
  const [fdtas, setFdtas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const { confirm, dialog } = useConfirm();

  const load = async () => {
    try {
      const [dmpRes, fdtaRes] = await Promise.all([
        matchingModelAPI.getDmps(),
        matchingModelAPI.getFdtas(),
      ]);
      setDmps(dmpRes.data.data);
      setFdtas(fdtaRes.data.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await matchingModelAPI.updateDmp(editing.id, payload);
        toast.success('Matching pair updated');
      } else {
        await matchingModelAPI.createDmp(payload);
        toast.success('Matching pair created');
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (dmp) => {
    if (!await confirm('Delete Matching Pair', `Delete "${dmp.name}"? This cannot be undone.`)) return;
    try {
      await matchingModelAPI.deleteDmp(dmp.id);
      toast.success('Deleted');
      await load();
    } catch {}
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  return (
    <div className="page-content-wide">
      <div className="page-header" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0, fontSize: 18 }}>Matching Pairs</h1>
        <button className="btn btn-primary" style={{ fontSize: 12 }}
          onClick={() => { setEditing(null); setModalOpen(true); }}>
          <Plus size={14} style={{ marginRight: 4 }} /> New Pair
        </button>
      </div>

      <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
        Matching pairs combine CV-side and Position-side field associations into comparison units.
        Each pair defines which fields from a CV are compared against which fields from a job position.
      </p>

      {dmps.length === 0 ? (
        <div className="section-box">
          <div className="section-box-body" style={{ textAlign: 'center', padding: 32, color: '#999' }}>
            No matching pairs yet. Create field associations first, then create pairs here.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dmps.map(dmp => {
            const cvLinks = (dmp.fdtaLinks || []).filter(l => l.fdta.side === 'CV');
            const posLinks = (dmp.fdtaLinks || []).filter(l => l.fdta.side === 'POSITION');
            const isExpanded = expanded[dmp.id];

            return (
              <div key={dmp.id} className="section-box">
                <div className="section-box-header"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '8px 12px' }}
                  onClick={() => toggleExpand(dmp.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span style={{ fontWeight: 600 }}>{dmp.name}</span>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      ({cvLinks.length} CV, {posLinks.length} Position)
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <button title="Edit" onClick={() => { setEditing(dmp); setModalOpen(true); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', padding: 4 }}>
                      <Pencil size={14} />
                    </button>
                    <button title="Delete" onClick={() => handleDelete(dmp)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ffcccc', padding: 4 }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="section-box-body" style={{ padding: '12px 16px' }}>
                    {dmp.description && (
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 12, fontStyle: 'italic' }}>
                        {dmp.description}
                      </div>
                    )}
                    <div className="form-row">
                      {/* CV side */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#0e4e95', textTransform: 'uppercase', marginBottom: 6 }}>
                          CV Side
                        </div>
                        {cvLinks.map(l => (
                          <div key={l.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <div style={{ fontWeight: 500 }}>{l.fdta.name}</div>
                            <div style={{ fontSize: 10, color: '#888' }}>{DATATYPES_SHORT[l.fdta.datatype]}</div>
                            {l.ordinalDistance && Array.isArray(l.ordinalDistance) && (
                              <div style={{ fontSize: 10, color: '#3D80CC', marginTop: 2 }}>
                                Distances: {l.ordinalDistance.join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Position side */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#8B4513', textTransform: 'uppercase', marginBottom: 6 }}>
                          Position Side
                        </div>
                        {posLinks.map(l => (
                          <div key={l.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <div style={{ fontWeight: 500 }}>{l.fdta.name}</div>
                            <div style={{ fontSize: 10, color: '#888' }}>{DATATYPES_SHORT[l.fdta.datatype]}</div>
                            {l.ordinalDistance && Array.isArray(l.ordinalDistance) && (
                              <div style={{ fontSize: 10, color: '#3D80CC', marginTop: 2 }}>
                                Distances: {l.ordinalDistance.join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <DmpModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave}
        initial={editing}
        allFdtas={fdtas}
        saving={saving}
      />
      {dialog}
    </div>
  );
};
